import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export type E2eTarget = 'dev' | 'prod';
export type E2eState = 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED';

export interface E2eExecution {
  id: string;
  target: E2eTarget;
  state: E2eState;
  startTime?: string;
  completionTime?: string;
}

export interface E2eDeleteResult {
  id: string;
  executionDeleted: boolean;
  filesDeleted: number;
  error?: string;
}

export interface E2eCleanupReport {
  deleted: E2eDeleteResult[];
  failed: E2eDeleteResult[];
  filesDeleted: number;
  skippedRunning: string[];
}

/** What a cleanup would remove — answered by the dry run, before anything goes. */
export interface E2eCleanupPlan {
  ids: string[];
  skippedRunning: string[];
}

export interface E2eReportUrls {
  reportUrl: string;
  downloadUrl: string;
}

export type E2eFixtureCategory = 'first' | 'followup-cd' | 'followup-uc';
export type E2eSuite = 'gateway' | 'doctor' | 'quiz';
export const E2E_SUITES: E2eSuite[] = ['gateway', 'doctor', 'quiz'];

export interface E2eFixture {
  _id: string;
  label: string;
  suite?: E2eSuite;
  category: E2eFixtureCategory;
  hasSubscription: boolean;
  proId: string;
  proName: string;
  enabled: boolean;
  notes: string;
  sourceProUserId: string;
  sourceProResultId?: string;
  createdBy: string;
  date_entered: string;
  date_changed: string;
  data: Record<string, any>;
}

export interface E2eFixtureCaptureReport {
  created: { fixtureId: string; label: string; category: string }[];
  errors: { proResultId: string; reason: string }[];
}

@Injectable({ providedIn: 'root' })
export class E2eTestsService {
  private apiUrl = '/api/e2e';

  constructor(private http: HttpClient) {}

  run(
    target: E2eTarget,
    includeSlow = false,
    suite: E2eSuite | 'all' = 'all',
  ): Observable<{ executionId: string }> {
    return this.http.post<{ executionId: string }>(`${this.apiUrl}/run`, {
      target,
      includeSlow,
      suite,
    });
  }

  executions(limit = 20): Observable<E2eExecution[]> {
    return this.http.get<E2eExecution[]>(`${this.apiUrl}/executions`, {
      params: { limit },
    });
  }

  /** Delete one execution together with its report objects in storage. */
  deleteExecution(id: string): Observable<E2eDeleteResult> {
    return this.http.delete<E2eDeleteResult>(`${this.apiUrl}/executions/${id}`);
  }

  /**
   * Bulk cleanup — pass `ids` for the selected runs, or `olderThanDays`
   * (with an optional `keepLast` floor) for an age-based sweep.
   */
  cleanupExecutions(body: {
    ids?: string[];
    olderThanDays?: number;
    keepLast?: number;
  }): Observable<E2eCleanupReport> {
    return this.http.post<E2eCleanupReport>(`${this.apiUrl}/executions/cleanup`, body);
  }

  /** Ask which runs a cleanup would remove, without removing them. */
  planCleanup(body: {
    ids?: string[];
    olderThanDays?: number;
    keepLast?: number;
  }): Observable<E2eCleanupPlan> {
    return this.http.post<E2eCleanupPlan>(`${this.apiUrl}/executions/cleanup`, {
      ...body,
      dryRun: true,
    });
  }

  reportUrls(executionId: string): Observable<E2eReportUrls> {
    return this.http.post<E2eReportUrls>(`${this.apiUrl}/report-ticket`, { executionId });
  }

  /**
   * Capture fixtures from explicitly chosen assessment results (ProResult ids).
   * The patient is derived server-side from the assessment, so the answers
   * stored are exactly the ones the operator picked.
   */
  captureFixtures(
    proResultIds: string[],
    suite: E2eSuite = 'gateway',
  ): Observable<E2eFixtureCaptureReport> {
    return this.http.post<E2eFixtureCaptureReport>(`${this.apiUrl}/fixtures/capture`, {
      proResultIds,
      suite,
    });
  }

  fixtures(): Observable<E2eFixture[]> {
    return this.http.get<E2eFixture[]>(`${this.apiUrl}/fixtures`);
  }

  updateFixture(
    id: string,
    patch: { label?: string; enabled?: boolean; notes?: string; suite?: E2eSuite },
  ): Observable<E2eFixture> {
    return this.http.put<E2eFixture>(`${this.apiUrl}/fixtures/${id}`, patch);
  }

  deleteFixture(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.apiUrl}/fixtures/${id}`);
  }

  runFixture(id: string, target: E2eTarget = 'dev'): Observable<{ executionId: string }> {
    return this.http.post<{ executionId: string }>(
      `${this.apiUrl}/fixtures/${id}/run`,
      { target },
    );
  }
}
